import { ARTIFACT_ID } from 'domain/shared/artifact';
import { ArtifactUploaderInput } from 'domain/shared/artifact/input';
import { Box, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { DateInput, TextInput, useFormContext, WysiwygInput } from 'framework/form';
import { Loading } from 'framework/ui/mui-builder';
import React from 'react';
import { BillingRequestStatus, BillingScheduleItemResult } from '../billingService';
import {
  calculateTotal,
  getItemSource,
  getItemValue,
  getPeriodDisplayValue,
  isPeriodicBillingType,
  MAX_SIZE_ARTIFACT,
} from './utils';
import BillingRequestActionButtons from './BillingRequestActionButtons';
import { FormSection } from 'framework/ui/layout';
import { BillingRequestFormPeriodicBaseProps, RenderCommonFieldParams } from './types';

const BillingRequestFormPeriodicBase = ({
  isUpdate,
  commonFieldSize,
  renderExtraFields,
}: BillingRequestFormPeriodicBaseProps) => {
  const { data = {}, isSubmitEnabled } = useFormContext();

  const scheduleItems = (data.data as BillingScheduleItemResult[]) ?? [];

  const { totalAmount, currency } = calculateTotal(scheduleItems);

  const isPeriodic = isPeriodicBillingType(data.type || '');
  const status = data.status;
  const inApprovalStatus = status === BillingRequestStatus.IN_APPROVAL;

  const renderCommonField = ({ item, index, isUpdate: updateMode, isPeriodic: periodic }: RenderCommonFieldParams) => {
    const isChangeRequest = item.type === 'CHANGE_REQUEST';
    const displayValue = getPeriodDisplayValue(item, updateMode);
    const itemSource = getItemSource(index, updateMode, periodic);
    const itemValue = getItemValue(item, updateMode, periodic);

    if (isChangeRequest) {
      return (
        <TextInput source={`data.${index}.changeRequestAlias`} label="Change request" disabled value={displayValue} />
      );
    }

    return (
      <TextInput source={itemSource} disabled label={periodic ? 'Period' : 'Billed milestone'} value={itemValue} />
    );
  };

  return (
    <FormSection>
      <Stack spacing={2} mb={4} sx={{ position: 'relative' }}>
        {!isSubmitEnabled && <Loading hasBackground />}
        <Grid container spacing={2}>
          {scheduleItems.map((item, index) => (
            <React.Fragment key={item.id || `item-${index}`}>
              <Grid size={commonFieldSize}>
                {renderCommonField({
                  item,
                  index,
                  isUpdate: Boolean(isUpdate),
                  isPeriodic,
                })}
              </Grid>
              {renderExtraFields({ item, index, inApprovalStatus, isPeriodic })}
            </React.Fragment>
          ))}
          {scheduleItems.length > 1 && (
            <Grid size={8}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Total: {totalAmount.toLocaleString()} {currency}
                </Typography>
              </Box>
            </Grid>
          )}
          {!isPeriodic && (
            <>
              <Grid size={2}>
                <DateInput source="startDate" label="Start date" disabled={inApprovalStatus} />
              </Grid>
              <Grid size={2}>
                <DateInput source="endDate" label="End date" disabled={inApprovalStatus} />
              </Grid>
            </>
          )}
          <Grid size={8}>
            <WysiwygInput source="note" label="Note" disabled={inApprovalStatus} />
          </Grid>
        </Grid>
        <ArtifactUploaderInput
          maxElements={1}
          maximumSize={MAX_SIZE_ARTIFACT}
          label="Service details"
          source="serviceDetailsArtifactId"
          artifactTypeId={ARTIFACT_ID.ARTIFACT_STORAGE_FILE}
          disabled={inApprovalStatus}
        />
        <ArtifactUploaderInput
          maxElements={10}
          maximumSize={MAX_SIZE_ARTIFACT}
          label="Reimbursement documentation"
          source="reimbursementDocumentationArtifactIds"
          artifactTypeId={ARTIFACT_ID.ARTIFACT_STORAGE_FILE}
          disabled={inApprovalStatus}
        />
        {!inApprovalStatus && <BillingRequestActionButtons />}
      </Stack>
    </FormSection>
  );
};

export default BillingRequestFormPeriodicBase;
